import React, { forwardRef } from "react";
import { toast } from "sonner";
import { CheckCircle2, Wallet } from "lucide-react";
import { api } from "../lib/api";
import { euro, fmtDate } from "../lib/format";
import { Switch } from "./ui/switch";

const ST = {
  pagato: ["Pagato", "bg-emerald-100 text-emerald-700"],
  bonifico_segnalato: ["Bonifico segnalato", "bg-sky-100 text-sky-700"],
  in_attesa: ["Da pagare", "bg-amber-100 text-amber-700"],
  da_pagare: ["Da pagare", "bg-amber-100 text-amber-700"],
  non_richiesto: ["Non richiesto", "bg-slate-100 text-slate-500"],
};

function Chip({ label, status, testid }) {
  const [t, cls] = ST[status] || ST.da_pagare;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>
      {label}: {t}
    </span>
  );
}

function AdminToggles({ quote, depPaid, saldoPaid, onUpdated }) {
  const toggle = async (kind, paid) => {
    try {
      await api.post(`/quotes/${quote.id}/confirm-deposit`, { kind, paid });
      const { data } = await api.get(`/public/quotes/${quote.public_token}`);
      onUpdated(data);
      toast.success(`${kind === "saldo" ? "Saldo" : "Acconto"} ${paid ? "segnato come incassato" : "segnato come non incassato"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non consentita (accedi come professionista)");
    }
  };
  return (
    <div data-testid="admin-payment-toggles" className="mb-4 rounded-xl border border-dashed border-indigo-300 bg-indigo-50/50 px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2">
      <span className="text-xs font-bold uppercase tracking-wider text-indigo-600">Controllo professionista</span>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <Switch data-testid="admin-toggle-deposit-paid" checked={depPaid} onCheckedChange={(v) => toggle("acconto", v)} /> Acconto incassato
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <Switch data-testid="admin-toggle-balance-paid" checked={saldoPaid} onCheckedChange={(v) => toggle("saldo", v)} /> Saldo incassato
      </label>
    </div>
  );
}

function AmountsSummary({ quote, payment, bal, depRequired, depPaid, saldoPaid, cur }) {
  const issuer = quote.issuer?.display_name || "Il Professionista";
  return (
    <div data-testid="payment-summary" className="mb-8 rounded-2xl border border-slate-200 bg-card p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3"><Wallet style={{ width: 18, height: 18 }} className="text-primary" /><p className="font-semibold text-slate-900">Riepilogo pagamenti</p></div>
      <div className="text-sm divide-y divide-slate-100">
        <div className="flex justify-between py-2"><span className="text-slate-500">Totale Preventivo</span><span className="font-mono font-semibold">{euro(bal.total, cur)}</span></div>
        {depRequired && (
          <div className="flex justify-between py-2">
            <span className={depPaid ? "text-emerald-700" : "text-slate-700"}>Acconto {depPaid ? "versato" : "da versare"}</span>
            <span data-testid="payment-summary-deposit" className={`font-mono font-semibold ${depPaid ? "text-emerald-600" : ""}`}>{depPaid ? "− " : ""}{euro(payment.deposit?.amount || bal.deposit_paid_amount || 0, cur)}</span>
          </div>
        )}
        <div className="flex justify-between py-2">
          <span className="font-bold text-slate-900">{saldoPaid ? "Saldo versato" : "Saldo residuo"}</span>
          <span data-testid="payment-summary-balance" className="font-mono font-extrabold text-slate-900">{euro(saldoPaid ? 0 : bal.amount, cur)}</span>
        </div>
      </div>
      {!(saldoPaid && (!depRequired || depPaid)) && (
        <p data-testid="payment-summary-note" className="text-xs text-slate-500 mt-3 break-words">Per le modalità di pagamento fai riferimento a {issuer}.</p>
      )}
    </div>
  );
}

const PaymentSection = forwardRef(function PaymentSection({ quote, cur, isPreview, onUpdated }, ref) {
  if (quote.status !== "accettato" || !quote.payment) return null;
  const payment = quote.payment;
  const bal = quote.balance || { total: 0, deposit_paid_amount: 0, amount: 0, status: "da_pagare" };
  const depRequired = payment.status !== "non_richiesto";
  const depPaid = payment.status === "pagato";
  const saldoPaid = bal.status === "pagato";

  return (
    <div ref={ref} id="payment-box" data-testid="payment-section" className="scroll-mt-6">
      {isPreview && <AdminToggles quote={quote} depPaid={depPaid} saldoPaid={saldoPaid} onUpdated={onUpdated} />}

      <div className="mb-4 flex flex-wrap gap-2">
        <Chip label="Acconto" status={payment.status} testid="deposit-status-chip" />
        <Chip label="Saldo" status={bal.status} testid="balance-status-chip" />
      </div>

      {depPaid && (
        <div data-testid="deposit-paid-banner" className="mb-6 flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 qf-pop">
          <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
          <div>
            <p className="font-semibold text-emerald-800">
              Acconto di {euro(payment.deposit?.amount || 0, cur)} ricevuto{saldoPaid ? "" : " — Lavori in corso"}
            </p>
            {payment.paid_at && <p className="text-sm text-emerald-700">Pagamento registrato il {fmtDate(payment.paid_at)}.</p>}
          </div>
        </div>
      )}

      {saldoPaid && (
        <div data-testid="balance-paid-banner" className="mb-6 flex items-center gap-3 bg-emerald-600 text-white rounded-xl px-5 py-4 qf-pop">
          <CheckCircle2 className="w-6 h-6 shrink-0" />
          <p className="font-semibold">Saldo finale ricevuto{bal.paid_at ? ` il ${fmtDate(bal.paid_at)}` : ""} — Preventivo interamente pagato. Grazie!</p>
        </div>
      )}

      <AmountsSummary {...{ quote, payment, bal, depRequired, depPaid, saldoPaid, cur }} />
    </div>
  );
});

export default PaymentSection;
